// ElevationsView.tsx — elevation-based editing view compositor
// Layout: ElevationSidebar | Viewport (2D/3D toggle) | SitePropsPanel
// Checkboxes = viewport visibility (all checked by default)
// Single-click highlight = changes right pane (props panel)

import { useState, useEffect, useRef, useCallback } from 'react';
import { useShallowStore, useStore } from '../store/useStore';
import ElevationSidebar from './ElevationSidebar';
import SiteViewport2D from './SiteViewport2D';
import SiteViewport3D from './SiteViewport3D';
import SitePropsPanel from './SitePropsPanel';
import ResizeHandle from './ResizeHandle';

interface ContextMenuInfo {
  panelId: string;
  elevationId: string | null;
  x: number;
  y: number;
}

export default function ElevationsView() {
  const {
    project,
    ui,
    addElevation,
    removeElevation,
    renameElevation,
    reorderElevation,
    reorderPanelInElevation,
    splitElevation,
    joinElevations,
    sortElevation,
    movePanelBetweenElevations,
    setElevationCorner,
    setElevationConnection,
    assignPanelToElevation,
    unassignPanelFromElevation,
    clonePanelAtPosition,
    setEditMode,
    setActivePanelId,
    elevationViewMode,
    toggleSidebar,
    toggleProps,
    selectedPanelIds,
    selectPanel,
    toggleSelectPanel,
    setSelectedPanels,
    clearPanelSelection,
    copySelectedPanels,
    pastePanelsToElevation,
    deleteSelectedPanels,
    panelClipboard,
    activeFloorId,
    floors,
    removeFloor,
    cloneFloor,
    setActiveFloorId,
  } = useShallowStore(s => ({
    project: s.project,
    ui: s.ui,
    addElevation: s.addElevation,
    removeElevation: s.removeElevation,
    renameElevation: s.renameElevation,
    reorderElevation: s.reorderElevation,
    reorderPanelInElevation: s.reorderPanelInElevation,
    splitElevation: s.splitElevation,
    joinElevations: s.joinElevations,
    sortElevation: s.sortElevation,
    movePanelBetweenElevations: s.movePanelBetweenElevations,
    setElevationCorner: s.setElevationCorner,
    setElevationConnection: s.setElevationConnection,
    assignPanelToElevation: s.assignPanelToElevation,
    unassignPanelFromElevation: s.unassignPanelFromElevation,
    clonePanelAtPosition: s.clonePanelAtPosition,
    setEditMode: s.setEditMode,
    setActivePanelId: s.setActivePanelId,
    elevationViewMode: s.ui.elevationViewMode,
    toggleSidebar: s.toggleSidebar,
    toggleProps: s.toggleProps,
    selectedPanelIds: s.ui.selectedPanelIds,
    selectPanel: s.selectPanel,
    toggleSelectPanel: s.toggleSelectPanel,
    setSelectedPanels: s.setSelectedPanels,
    clearPanelSelection: s.clearPanelSelection,
    copySelectedPanels: s.copySelectedPanels,
    pastePanelsToElevation: s.pastePanelsToElevation,
    deleteSelectedPanels: s.deleteSelectedPanels,
    panelClipboard: s.ui.panelClipboard,
    activeFloorId:  s.ui.activeFloorId,
    floors:         s.project?.floors,
    // addFloor not used — "+" button clones floor 0 via cloneFloor
    removeFloor:    s.removeFloor,
    cloneFloor:     s.cloneFloor,
    setActiveFloorId: s.setActiveFloorId,
  }));

  const [selectedElevId, setSelectedElevId] = useState<string | null>(null);
  const [checkedElevIds, setCheckedElevIds] = useState<Set<string> | null>(null);
  const [ctxMenu, setCtxMenu] = useState<ContextMenuInfo | null>(null);
  const [sidebarW, setSidebarW] = useState(() => Math.round(window.innerWidth * 0.2));
  const [propsW, setPropsW] = useState(() => Math.round(window.innerWidth * 0.2));
  const initRef = useRef(false);

  const onSidebarResize = useCallback((dx: number) => {
    setSidebarW(w => Math.max(80, Math.min(400, w + dx)));
  }, []);
  const onPropsResize = useCallback((dx: number) => {
    setPropsW(w => Math.max(80, Math.min(500, w - dx)));
  }, []);

  // Close context menu on any outside click
  useEffect(() => {
    if (!ctxMenu) return;
    function close() { setCtxMenu(null); }
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [ctxMenu]);

  // Reset checked state when a different project is loaded so all floors get re-initialized
  const projectId = project?.id;
  useEffect(() => {
    setCheckedElevIds(null);
    initRef.current = false;
  }, [projectId]);

  if (!project) return null;

  const allProjectElevations = project.elevations ?? [];
  const allProjectFloors     = floors ?? [];
  // Active floor: use activeFloorId from store; fall back to first floor
  const activeFloor = allProjectFloors.find(f => f.id === activeFloorId) ?? allProjectFloors[0] ?? null;
  const activeFloorElevIds = new Set(activeFloor?.elevationIds ?? allProjectElevations.map(e => e.id));
  // Elevations shown in the sidebar = active floor's elevations
  const elevations = allProjectElevations.filter(e => activeFloorElevIds.has(e.id));

  // Initialize: on first load check ALL elevations across ALL floors so every floor renders in 3D.
  // On floor tab switch (initRef reset, checkedElevIds kept): only add active floor elevations if none are checked yet.
  if (!initRef.current && allProjectElevations.length > 0) {
    initRef.current = true;
    if (checkedElevIds === null) {
      // First load — check everything
      setCheckedElevIds(new Set(allProjectElevations.map(e => e.id)));
    } else {
      // Floor tab switch — add active floor elevations if not yet in set
      const activeFloorHasChecked = elevations.some(e => checkedElevIds.has(e.id));
      if (!activeFloorHasChecked) {
        setCheckedElevIds(prev => new Set([...(prev ?? []), ...elevations.map(e => e.id)]));
      }
    }
    setSelectedElevId(s => s && elevations.some(e => e.id === s) ? s : (elevations[0]?.id ?? null));
  }

  // Ensure checked set stays in sync when elevations are added/removed
  const checkedSet = checkedElevIds ?? new Set(elevations.map(e => e.id));

  const selectedElev = elevations.find(e => e.id === selectedElevId) ?? null;
  const checkedElevations = elevations.filter(e => checkedSet.has(e.id));

  // Auto-select first if current selection removed
  if (!selectedElev && elevations.length > 0 && selectedElevId !== null) {
    setSelectedElevId(elevations[0].id);
  }

  function handlePanelDoubleClick(panelId: string) {
    setActivePanelId(panelId);
    setEditMode('panel');
  }

  function handlePanelClick(panelId: string, ctrlKey: boolean) {
    if (ctrlKey) {
      toggleSelectPanel(panelId);
    } else {
      selectPanel(panelId);
    }
  }

  function handleRubberbandSelect(ids: string[]) {
    setSelectedPanels(ids);
  }

  // Keyboard shortcuts for panel selection
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // Only handle when in site mode
      const state = useStore.getState();
      if (state.ui.editMode !== 'elevations') return;
      // Don't capture when an input/textarea/select is focused
      const tag = (document.activeElement?.tagName ?? '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (state.ui.selectedPanelIds.length > 0) {
          e.preventDefault();
          deleteSelectedPanels();
        }
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (state.ui.selectedPanelIds.length > 0) {
          e.preventDefault();
          copySelectedPanels();
        }
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        if (state.ui.panelClipboard) {
          e.preventDefault();
          // Find which elevation the first selected panel belongs to, or use highlighted elevation
          const selId = state.ui.selectedPanelIds[0];
          let targetElev = selectedElevId;
          if (selId) {
            const elev = (state.project?.elevations ?? []).find(el => el.panelIds.includes(selId));
            if (elev) targetElev = elev.id;
          }
          if (targetElev) {
            pastePanelsToElevation(targetElev, selId);
          }
        }
      } else if (e.key === 'Escape') {
        if (state.ui.selectedPanelIds.length > 0) {
          clearPanelSelection();
        }
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedElevId, deleteSelectedPanels, copySelectedPanels, pastePanelsToElevation, clearPanelSelection]);

  function toggleElevCheck(id: string) {
    setCheckedElevIds(prev => {
      const next = new Set(prev ?? elevations.map(e => e.id));
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function checkAllElevations() {
    setCheckedElevIds(new Set(elevations.map(e => e.id)));
  }

  function uncheckAllElevations() {
    setCheckedElevIds(new Set());
  }

  // Split wrapper: auto-check new sections in the sidebar
  function handleSplit(id: string, sections: number, panelsPerSection: number[]) {
    const before = new Set(elevations.map(e => e.id));
    splitElevation(id, sections, panelsPerSection);
    const after = useStore.getState().project?.elevations ?? [];
    setCheckedElevIds(prev => {
      const next = new Set(prev ?? elevations.map(e => e.id));
      next.delete(id);
      for (const e of after) {
        if (!before.has(e.id)) next.add(e.id);
      }
      return next;
    });
  }

  // Join wrapper: auto-check the new joined elevation
  function handleJoin(ids: string[]) {
    const before = new Set(elevations.map(e => e.id));
    joinElevations(ids);
    const after = useStore.getState().project?.elevations ?? [];
    setCheckedElevIds(prev => {
      const next = new Set(prev ?? elevations.map(e => e.id));
      for (const id of ids) next.delete(id);
      for (const e of after) {
        if (!before.has(e.id)) next.add(e.id);
      }
      return next;
    });
  }

  // Context menu handlers
  function handlePanelContextMenu(panelId: string, screenX: number, screenY: number) {
    // Find which elevation this panel belongs to
    const elev = elevations.find(e => e.panelIds.includes(panelId));
    setCtxMenu({ panelId, elevationId: elev?.id ?? null, x: screenX, y: screenY });
    // Also select the panel if not already selected
    if (!selectedPanelIds.includes(panelId)) {
      selectPanel(panelId);
    }
  }

  function handleSplitLeft() {
    if (!ctxMenu) return;
    const elevId = ctxMenu.elevationId ?? selectedElev?.id;
    if (!elevId) return;
    clonePanelAtPosition(ctxMenu.panelId, elevId, 'before');
    setCtxMenu(null);
  }

  function handleSplitRight() {
    if (!ctxMenu) return;
    const elevId = ctxMenu.elevationId ?? selectedElev?.id;
    if (!elevId) return;
    clonePanelAtPosition(ctxMenu.panelId, elevId, 'after');
    setCtxMenu(null);
  }

  function handleCtxCopy() {
    if (!ctxMenu) return;
    selectPanel(ctxMenu.panelId);
    copySelectedPanels();
    setCtxMenu(null);
  }

  function handleCtxDelete() {
    if (!ctxMenu) return;
    if (!selectedPanelIds.includes(ctxMenu.panelId)) selectPanel(ctxMenu.panelId);
    // Small delay so selectPanel updates store before delete reads it
    setTimeout(() => { deleteSelectedPanels(); }, 0);
    setCtxMenu(null);
  }

  return (
    <div className="site-view">
      <aside
        className={`sidebar${ui.sidebarOpen ? '' : ' collapsed'}`}
        style={ui.sidebarOpen ? { width: sidebarW, minWidth: sidebarW } : undefined}
      >
        {/* ── Floor switcher ────────────────────────────────────────────── */}
        {allProjectFloors.length > 0 && (
          <div className="floor-switcher">
            {allProjectFloors.map(f => (
              <button
                key={f.id}
                className={`floor-tab${f.id === activeFloorId ? ' active' : ''}`}
                onClick={() => { setActiveFloorId(f.id); initRef.current = false; }}
                title={f.type === 'mezzanine' ? `Mezzanine at ${f.ledgerHeight ?? '?'}″` : f.name}
              >
                {f.name}
              </button>
            ))}
            <button
              className="floor-tab floor-tab-add"
              title="Add floor (clone Level 1)"
              onClick={() => cloneFloor(allProjectFloors[0]?.id ?? '')}
            >
              +
            </button>
            {allProjectFloors.length > 1 && activeFloor && (
              <button
                className="floor-tab floor-tab-clone"
                title={`Clone ${activeFloor.name}`}
                onClick={() => cloneFloor(activeFloor.id)}
              >
                ⧉
              </button>
            )}
            {allProjectFloors.length > 1 && activeFloor && (
              <button
                className="floor-tab floor-tab-remove"
                title={`Remove ${activeFloor.name}`}
                onClick={() => { if (window.confirm(`Remove "${activeFloor.name}" and all its panels?`)) removeFloor(activeFloor.id); }}
              >
                ✕
              </button>
            )}
          </div>
        )}
        <ElevationSidebar
          elevations={elevations}
          selectedId={selectedElevId}
          checkedIds={checkedSet}
          onSelect={setSelectedElevId}
          onToggleCheck={toggleElevCheck}
          onCheckAll={checkAllElevations}
          onUncheckAll={uncheckAllElevations}
          onAdd={addElevation}
          onRemove={removeElevation}
          onRename={renameElevation}
          onReorder={reorderElevation}
          onJoin={handleJoin}
          collapsed={!ui.sidebarOpen}
          onToggleCollapse={toggleSidebar}
        />
      </aside>

      {ui.sidebarOpen && <ResizeHandle onResize={onSidebarResize} />}

      <div className="site-view-viewport">
        {elevationViewMode === '2d' ? (
          <SiteViewport2D
            elevations={checkedElevations}
            allElevations={elevations}
            highlightId={selectedElevId}
            panels={project.panels}
            selectedPanelIds={selectedPanelIds}
            onPanelClick={handlePanelClick}
            onPanelDoubleClick={handlePanelDoubleClick}
            onPanelContextMenu={handlePanelContextMenu}
            onElevationClick={setSelectedElevId}
            onReorderPanel={reorderPanelInElevation}
            onMovePanelBetween={movePanelBetweenElevations}
            onSort={sortElevation}
            onSplit={handleSplit}
            onSetCorner={setElevationCorner}
            onSetConnection={setElevationConnection}
            onRubberbandSelect={handleRubberbandSelect}
          />
        ) : (
          <SiteViewport3D
            elevations={checkedElevations}
            panels={project.panels}
            jointWidth={project.settings.jointWidth}
            highlightId={selectedElevId}
            selectedPanelIds={selectedPanelIds}
            onPanelClick={handlePanelClick}
            onPanelDoubleClick={handlePanelDoubleClick}
            allFloors={allProjectFloors.length > 1 ? allProjectFloors : undefined}
            allElevations={allProjectFloors.length > 1 ? allProjectElevations : undefined}
            checkedElevIds={allProjectFloors.length > 1 ? checkedSet : undefined}
          />
        )}

        {/* Context menu for split left / split right */}
        {ctxMenu && (
          <div
            className="cv-context-menu"
            style={{ left: ctxMenu.x, top: ctxMenu.y }}
            onClick={e => e.stopPropagation()}
          >
            <button className="cv-context-menu-item" onClick={handleSplitLeft}>
              ← Split Left
            </button>
            <button className="cv-context-menu-item" onClick={handleSplitRight}>
              Split Right →
            </button>
            <div className="cv-context-menu-sep" />
            <button className="cv-context-menu-item" onClick={handleCtxCopy}>
              Copy Panel
            </button>
            {panelClipboard && ctxMenu.elevationId && (
              <button className="cv-context-menu-item" onClick={() => {
                pastePanelsToElevation(ctxMenu.elevationId!, ctxMenu.panelId);
                setCtxMenu(null);
              }}>
                Paste After
              </button>
            )}
            <button className="cv-context-menu-item cv-context-menu-danger" onClick={handleCtxDelete}>
              Delete Panel
            </button>
          </div>
        )}
      </div>

      {ui.propsOpen && <ResizeHandle onResize={onPropsResize} />}

      <aside
        className={`props-panel${ui.propsOpen ? '' : ' collapsed'}`}
        style={ui.propsOpen ? { width: propsW, minWidth: propsW } : undefined}
      >
        <SitePropsPanel
          elevation={selectedElev}
          allPanels={project.panels}
          allElevations={elevations}
          onAssign={assignPanelToElevation}
          onUnassign={unassignPanelFromElevation}
          collapsed={!ui.propsOpen}
          onToggleCollapse={toggleProps}
        />
      </aside>
    </div>
  );
}
